import { useEffect } from 'react'
import { Button, IconClose, Typography } from '@grammarly/badger-ui'
import { navHelper } from '../../../lib/services/navigation'
import { Modal, DEFAULT_ROOT_ELEMENT_SELECTOR } from './index'
import { ButtonCta, ButtonColor } from '../../ui-lib/button'
import { useToolsLtvNav } from '../../../lib/hooks'

import styles from './free-user-trial-modal.module.scss'
import { FormattedMessage, useIntl } from 'react-intl'

interface FreeUserTrialModalProps {
  show: boolean
  close: () => void
  appElementForModal?: string | HTMLElement
}

const getRootSelector = (appElementForModal?: string | HTMLElement): string => {
  if (typeof appElementForModal === 'string') {
    return appElementForModal
  }
  if (appElementForModal?.id) {
    return `#${appElementForModal.id}`
  }
  return DEFAULT_ROOT_ELEMENT_SELECTOR
}

export const FreeUserTrialModal = (props: FreeUserTrialModalProps) => {
  const { show, close, appElementForModal } = props
  const intl = useIntl()
  const { ctaText, description, trackShow, isLtvNavExperiment } = useToolsLtvNav()

  const titleId = 'free-user-trial-modal-title'
  const rootSelector = getRootSelector(appElementForModal)

  useEffect(() => {
    if (show) {
      trackShow()
    }
  }, [show, trackShow])

  return (
    <Modal
      isOpen={show}
      onRequestClose={close}
      aria={{ labelledby: titleId }}
      rootElementSelector={rootSelector}
      className={styles.modal}
    >
      <div className={styles.content}>
        <Button
          className={styles.closeButton}
          padding="compact"
          onClick={close}
          aria-label={intl.formatMessage({
            id: 'global.uiAction.close',
            description: 'Text for CTA that closes an element',
            defaultMessage: 'Close',
          })}
        >
          <IconClose aria-hidden="true" />
        </Button>
        <Typography.TM as="h2" id={titleId}>
          <b>
            <FormattedMessage
              id="modal.freeUserTrial.title"
              description="Modal - Free User Trial - Title"
              defaultMessage="Unlock the full power of Grammarly"
            />
          </b>
        </Typography.TM>
        <Typography.TS as="p" className={styles.description}>
          {description}
        </Typography.TS>
        <ButtonCta
          className={styles.upgradeButton}
          onClick={() => navHelper.navigateUrl('/plans')}
          text={ctaText}
          color={ButtonColor.WDK24PrimaryLightBg}
          showArrow
        />
        <ButtonCta
          className={styles.stayFreeButton}
          onClick={close}
          text="Stay on Free plan"
          color={ButtonColor.WDK24WhiteBlackText}
        />
        {!isLtvNavExperiment && (
          <Typography.TS as="p" className={styles.disclaimer}>
            <FormattedMessage
              id="modal.freeUserTrial.disclaimer"
              description="Modal - Free User Trial - Disclaimer"
              defaultMessage="Offer available to eligible users."
            />
          </Typography.TS>
        )}
      </div>
    </Modal>
  )
}
